"use client";

import type { CSSProperties } from "react";
import {
	ArtistIcon,
	type ArtistIconName,
} from "@/components/design-system/ArtistDesignSystem";
import type { LibraryMetric } from "./library-data";

export interface MetricPanelProps {
	readonly metric: LibraryMetric;
	readonly additionalMetrics: readonly LibraryMetric[];
	readonly showAllMetrics: boolean;
	readonly onToggle: () => void;
}

const panelStyle: CSSProperties = {
	display: "grid",
	gap: 12,
	justifyItems: "start",
	textAlign: "left",
};

const titleStyle: CSSProperties = {
	margin: 0,
	fontSize: "1rem",
	fontWeight: 650,
	color:
		"color-mix(in display-p3, var(--ds-on-media-primary) 95%, transparent)",
};

const valueStyle: CSSProperties = {
	margin: 0,
	fontSize: "2.1rem",
	fontWeight: 800,
	lineHeight: 1.05,
	color: "var(--ds-on-media-primary)",
	fontVariantNumeric: "tabular-nums",
};

const captionStyle: CSSProperties = {
	margin: 0,
	fontSize: "0.82rem",
	color:
		"color-mix(in display-p3, var(--ds-on-media-primary) 80%, transparent)",
};

/**
 * MetricPanel — primary metric + show more/less toggle that reveals the 4
 * additional metrics. Mirrors `MetricPanel` on iOS (headline title, 34pt bold
 * rounded value, footnote caption, capsule toggle button).
 */
export function MetricPanel({
	metric,
	additionalMetrics,
	showAllMetrics,
	onToggle,
}: Readonly<MetricPanelProps>) {
	return (
		<div className="library-metric-panel" style={panelStyle}>
			<MetricRow metric={metric} valueSize="lg" />
			{additionalMetrics.length === 0 ? null : (
				<button
					type="button"
					className="library-metric-toggle"
					onClick={onToggle}
					aria-expanded={showAllMetrics}
				>
					<span>{showAllMetrics ? "收起" : "展开更多"}</span>
					<ArtistIcon
						name="chevron"
						size={14}
						strokeWidth={2.4}
						style={{
							transform: showAllMetrics ? "rotate(-90deg)" : "rotate(90deg)",
						}}
					/>
				</button>
			)}
			{showAllMetrics && additionalMetrics.length > 0 ? (
				<div
					className="library-metric-panel__additional"
					style={{
						display: "grid",
						gap: 12,
						justifyItems: "start",
						textAlign: "left",
						width: "100%",
					}}
				>
					{additionalMetrics.map((item) => (
						<MetricRow key={item.id} metric={item} valueSize="md" />
					))}
				</div>
			) : null}
		</div>
	);
}

function MetricRow({
	metric,
	valueSize,
}: Readonly<{ metric: LibraryMetric; valueSize: "lg" | "md" }>) {
	const resolvedValueStyle: CSSProperties =
		valueSize === "lg"
			? valueStyle
			: { ...valueStyle, fontSize: "1.75rem", fontWeight: 650 };
	return (
		<div
			className="library-metric"
			style={{ display: "grid", gap: 4, justifyItems: "start" }}
		>
			<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
				<ArtistIcon
					name={metric.iconName satisfies ArtistIconName}
					size={16}
					strokeWidth={2}
				/>
				<h3 style={titleStyle}>{metric.title}</h3>
			</div>
			<p style={resolvedValueStyle}>{metric.value}</p>
			<p style={captionStyle}>{metric.caption}</p>
		</div>
	);
}
